/* eslint-disable no-underscore-dangle */
import React, { Fragment, useState, useEffect, useReducer, useContext } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import Loader from 'LegacyWeployDesignLanguage/loaders/Loader';
import { AddIcon } from 'LegacyWeployDesignLanguage/icons';
import { Tabs, Tab } from 'LegacyWeployDesignLanguage/Tabs';
import Select from 'LegacyWeployDesignLanguage/inputs/Select';
import Snackbar from 'LegacyWeployDesignLanguage/Snackbar';
import Grid from 'LegacyWeployDesignLanguage/Grid';
import Button from 'WeployDesignLanguage/buttons/Button';

import NotificationDialog from '../../../components/weployer/common/NotificationDialog';
import ActiveJobs from '../../../components/weployer/jobs/ActiveJobs';
import JobsComponent from '../../../components/weployer/jobs/Jobs';
import Subheader from '../../../components/weployer/common/Subheader';
import PastJobs from '../../../components/weployer/jobs/PastJobs';
import InfiniteScroll from '../InfiniteScroll';
import JobTypeSelectionModal, { JOB_TYPES } from '../jobTypeSelectionModal/JobTypeSelectionModal';
import {
  JOBS_TEMPLATES_PATH,
  JOB_DETAILS_PATH,
  TRANSFER_JOB_PATH,
  VIEW_SHORTLIST_PATH,
  VIEW_REHIRE_SHORTLIST_PATH,
  getExternalPath,
  getInternalPath,
} from '../pathHelpers';
import EmptyOpenJobs from '../../../components/weployer/jobs/EmptyOpenJobs';
import {
  activeJobs,
  upcomingJobs,
  openJobs,
  DISMISS,
  SELECT_WEPLOYEES,
  pastJobs,
} from '../common/mappers';
import {
  DASHBOARD_JOBS_FETCH_SUCCESS,
  JOB_DISMISS_CLICK,
  JOB_EXPIRE,
  PAST_JOBS_FETCH_SUCCESS,
} from '../common/actionTypes';
import {
  initialState,
  reducer,
} from '../common/reducer';
import {
  CURRENT_JOBS_STATUSES,
  ME,
  ORGANISATION,
  PENDING_CREDIT_APPROVAL,
  OWNED_BY_FILTER_KEY,
  AWAITING_SELECTION,
  OPEN,
  EXPIRED,
  PAST_JOBS_STATUSES,
} from '../common/graphql/constants';
import { GET_JOBS } from './graphql/queries';
import {
  trackPageView,
  trackEvent,
  JOBS,
  WEPLOYER,
  OWNER_FILTER_CHANGE,
} from '../../../utils/analytics';

import { PolicyContext, VIEW_ORGANISATION_JOBS } from '../PolicyProvider';

const POLL_INTERVAL = 30000;

/* Due to the recent pagination API that we roll out for jobs field, page size must be specified while fetching active jobs.
This obviously will cause issue if there are more than 200 jobs in those category. There is some UX change that we need to make so
these views can work with infinite scrolling */
const ACTIVE_JOB_PAGE_SIZE = 200;

const OWNED_BY_FILTERS = {
  [ME]: 'Owned by me',
  [ORGANISATION]: 'Owned by organisation',
};

const ACTIVE_JOBS_TAB = 'Active Jobs';
const PAST_JOBS_TAB = 'Past Jobs';

const TAB_NAMES = [
  ACTIVE_JOBS_TAB,
  PAST_JOBS_TAB,
];

const PAST_JOBS_PAGE_SIZE = 20;

const { Option } = Select;

const StyledButtonsContainer = styled.div`
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const StyledButton = styled(Button)`
  margin-right: ${props => props.theme.space.single};
`;

const StyledTabsContainer = styled(Tabs)`
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const StyledSubheader = styled(Subheader)`
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const StyledJobs = styled.div`
  margin-bottom: ${props => props.theme.spacing[3]};
`;

const Jobs = ({
  applicationConfig: { features: { PAST_WEPLOYEE_JOB_POSTING } },
  onWeployerDetailsChange,
  weployerDetails,
  navigateExternal,
  history,
}) => {
  const [store, dispatch] = useReducer(reducer, initialState);
  const [ownedByFilter, setOwnedByFilter] = useState(weployerDetails[OWNED_BY_FILTER_KEY]);
  const [refetchingFromFilter, setRefetchingFromFilter] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [modalVisible, setModalVisible] = useState(false);
  const [jobTypeSelectionModalVisible, setJobTypeSelectionModalVisible] = useState(false);

  const { actions } = useContext(PolicyContext);

  const { loading, data, error } = useQuery(GET_JOBS, {
    fetchPolicy: 'network-only',
    variables: { statuses: CURRENT_JOBS_STATUSES, ownedBy: ownedByFilter },
    first: ACTIVE_JOB_PAGE_SIZE,
    pollInterval: POLL_INTERVAL,
  });


  const { data: pastJobData, loading: pastJobQueryLoading, fetchMore: fetchMorePastJob } = useQuery(GET_JOBS, {
    variables: {
      statuses: PAST_JOBS_STATUSES,
      ownedBy: ownedByFilter,
      first: PAST_JOBS_PAGE_SIZE,
    },
  });

  const [pastJobCursor, setPastJobCursor] = useState('');
  const [hasMorePastJobs, setHasMorePastJobs] = useState(false);

  const handlePastJobEndReached = () => {
    const result = fetchMorePastJob({
      query: GET_JOBS,
      variables: {
        statuses: PAST_JOBS_STATUSES,
        ownedBy: ownedByFilter,
        first: PAST_JOBS_PAGE_SIZE,
        after: pastJobCursor,
      },
      updateQuery: (_, { fetchMoreResult }) => (fetchMoreResult),
    });
    return result;
  };

  useEffect(() => {
    if (!pastJobQueryLoading && pastJobData) {
      setPastJobCursor(get(pastJobData, 'weployer.jobs.pageInfo.endCursor'));
      setHasMorePastJobs(get(pastJobData, 'weployer.jobs.pageInfo.hasNextPage'));
      const payload = {
        jobs: [...pastJobData.weployer.jobs.nodes],
      };

      dispatch({
        type: PAST_JOBS_FETCH_SUCCESS,
        payload,
      });
    }
  }, [pastJobQueryLoading, pastJobData]);

  useEffect(() => trackPageView(WEPLOYER, JOBS), []);

  useEffect(() => {
    if (!loading && data) {
      const payload = {
        jobs: [...data.weployer.jobs.nodes],
      };

      dispatch({
        type: DASHBOARD_JOBS_FETCH_SUCCESS,
        payload,
        reset: refetchingFromFilter && tabIndex !== 2,
      });
      setRefetchingFromFilter(false);
    }
  }, [loading, data, error]);

  useEffect(() => {
    const { [OWNED_BY_FILTER_KEY]: ownedByFilterFromLocalStorage } = weployerDetails;

    if (ownedByFilterFromLocalStorage) {
      setOwnedByFilter(ownedByFilterFromLocalStorage);
    }
  }, [weployerDetails]);

  const {
    jobs,
    positions,
    weployees,
  } = store;

  const orderedPastJobs = pastJobs(jobs, positions, weployees, ownedByFilter === ME, actions);

  const navigateToSelectJob = (jobId) => {
    const { jobType } = jobs[jobId];
    const pathName = jobType === JOB_TYPES.REHIRE.toLowerCase() ? VIEW_REHIRE_SHORTLIST_PATH : VIEW_SHORTLIST_PATH;

    const path = getExternalPath({ pathName, pathParams: { jobId } });
    navigateExternal(path);
  };

  const handleJobCardClick = (jobId) => {
    const job = jobs[jobId];

    if (!job.instant && [OPEN, AWAITING_SELECTION, EXPIRED].includes(job.status)) {
      navigateToSelectJob(jobId);
    } else if (job.status !== PENDING_CREDIT_APPROVAL) {
      history.push((getInternalPath({ pathName: JOB_DETAILS_PATH, pathParams: { jobId } })));
    }
  };

  const handleExpiry = (jobId) => {
    dispatch({ type: JOB_EXPIRE, id: jobId });
  };

  const handleDismissJob = (jobId) => {
    dispatch({ type: JOB_DISMISS_CLICK, id: jobId });
  };

  const handlePostJobButtonClick = () => {
    if (PAST_WEPLOYEE_JOB_POSTING) {
      setJobTypeSelectionModalVisible(true);
    } else {
      history.push(getInternalPath({ pathName: JOBS_TEMPLATES_PATH }));
    }
  };

  const handleJobPostingStrategyModalClose = () => {
    setJobTypeSelectionModalVisible(false);
  };

  const handleOwnedByChange = (event) => {
    const { value } = event.target;

    setOwnedByFilter(value);
    setRefetchingFromFilter(true);
    onWeployerDetailsChange(OWNED_BY_FILTER_KEY, value);
    trackEvent(OWNER_FILTER_CHANGE, { ownedBy: value });
  };

  const handleTabClick = (_, index) => setTabIndex(index);

  const handleJobCardPrimaryButtonClick = (type, jobId) => {
    switch (type) {
      case SELECT_WEPLOYEES:
        navigateToSelectJob(jobId);
        break;
      case DISMISS:
        handleDismissJob(jobId);
        break;
      default:
        break;
    }
  };

  const handleModalClose = () => (
    setModalVisible(false)
  );

  const handleTransferJobClick = (jobId) => {
    if (jobs[jobId].isTransferrable) {
      const path = getExternalPath({ pathName: TRANSFER_JOB_PATH, pathParams: { jobId } });
      navigateExternal(path);
    } else {
      setModalVisible(true);
    }
  };

  const renderTabContent = (tab) => {
    const defaultTabContent = (
      <ActiveJobs
        currentJobs={activeJobs(jobs, positions, weployees, actions)}
        upcomingJobs={upcomingJobs(jobs, positions, weployees, actions)}
        onJobCardClick={handleJobCardClick}
        onTransferJobClick={handleTransferJobClick}
        navigateExternal={navigateExternal}
      />
    );

    switch (tab) {
      case ACTIVE_JOBS_TAB:
        return defaultTabContent;
      case PAST_JOBS_TAB:
        return (
          <InfiniteScroll onBottomReached={handlePastJobEndReached} isEndReached={!hasMorePastJobs}>
            <PastJobs
              jobs={orderedPastJobs}
              onJobCardClick={handleJobCardClick}
              onTransferJobClick={handleTransferJobClick}
            />
          </InfiniteScroll>
        );
      default:
        return defaultTabContent;
    }
  };

  return (
    <Fragment>
      <Snackbar
        visible={loading && refetchingFromFilter}
        message="Fetching jobs..."
      />
      <Grid container justify="space-between">
        <Grid item>
          <StyledButtonsContainer>
            <StyledButton
              startIcon={<AddIcon />}
              onClick={() => handlePostJobButtonClick()}
            >
              Post a job
            </StyledButton>
          </StyledButtonsContainer>
        </Grid>
        {
          actions.includes(VIEW_ORGANISATION_JOBS) &&
            <Grid item>
              <Select
                value={OWNED_BY_FILTERS[ownedByFilter]}
                type={Select.TEXT}
                onChange={handleOwnedByChange}
                name="ownedBy"
                id="owned-by"
              >
                <Option value={ME}>Owned by me</Option>
                <Option value={ORGANISATION}>Owned by Organisation</Option>
              </Select>
            </Grid>
        }
      </Grid>
      <Loader loading={loading && !data && !refetchingFromFilter} text="Loading..." >
        <StyledSubheader>Open Jobs</StyledSubheader>
        <StyledJobs>
          <JobsComponent
            jobs={openJobs(jobs, actions)}
            emptyState={<EmptyOpenJobs />}
            onPrimaryButtonClick={handleJobCardPrimaryButtonClick}
            onExpiry={handleExpiry}
            onJobCardClick={handleJobCardClick}
            onTransferJobClick={handleTransferJobClick}
          />
        </StyledJobs>
        <StyledTabsContainer
          value={tabIndex}
          onChange={handleTabClick}
        >
          <Tab label="Jobs" />
          <Tab label="Past Jobs" />
        </StyledTabsContainer>
        <div>
          {renderTabContent(TAB_NAMES[tabIndex])}
        </div>
        <NotificationDialog title="Job Transfer Failed" onModalClose={handleModalClose} open={modalVisible}>
          <p>Jobs payable by credit card cannot be transferred. Please contact your account director.</p>
        </NotificationDialog>
      </Loader>
      {jobTypeSelectionModalVisible &&
        <JobTypeSelectionModal
          open={jobTypeSelectionModalVisible}
          onJobPostingStrategyModalClose={handleJobPostingStrategyModalClose}
        />
      }
    </Fragment>
  );
};

Jobs.propTypes = {
  applicationConfig: PropTypes.object.isRequired,
  navigateExternal: PropTypes.func.isRequired,
  weployerDetails: PropTypes.object,
  onWeployerDetailsChange: PropTypes.func,
  history: PropTypes.object.isRequired,
};

Jobs.defaultProps = {
  weployerDetails: {},
  onWeployerDetailsChange: () => {},
};

export default withRouter(Jobs);
